import { ILoadBoardViewModel } from '@store/loadboard/types';
import { Carrier } from '@store/orders/types';
import { OrderLineItem } from '@typeDefinitions/lineItemTypes';
import { StopViewModel } from '@typeDefinitions/stopTypes';
import { carrierDefaultsT } from '@typeDefinitions/capacityTypes';

export type CarrierTypeT = 'owned' | 'developing' | 'recent';

export type TempCarrierT = {
  name: string;
  id: number;
  type: CarrierTypeT;
};

export type CachedCarrierT = {
  carrierId: number;
  carrierName: string;
  noCapacityForTheDay: boolean;
  hasBeenCalledForTheDay: boolean;
};

export type CapacitySidepanelModelT = {
  owned: CachedCarrierT[];
  developing: CachedCarrierT[];
  recent: CachedCarrierT[];
  isCapacityTeamEnabled: boolean;
};

export type CarrierSectionT = {
  type: CarrierTypeT;
  name: string;
  shouldShow: () => boolean;
};

export type CapacityStoreT = {
  carriers: CapacitySidepanelModelT;
  selectedLoads: Record<number, boolean>;
  equipment: EquipmentT[];
  loads: ILoadBoardViewModel[];
  isLoading: boolean;
  selectedTruckId: number | undefined;
  offers: OfferT[];
  offerAmounts: Record<number, number>;
};

export enum TruckCapacityStatusEnum {
  HasCapacity = 'HasCapacity',
  NoCapacity = 'NoCapacity',
}

export type ConsistentTruck = {
  id: number;
  carrierId: number;
  isConsistent: boolean;
  destinationCity: string;
  destinationCountry: string;
  destinationLatitude: number | null;
  destinationLongitude: number | null;
  destinationPostal: string;
  destinationRadiusMiles: number | null;
  destinationState: string;
  equipment: string;
  notes: string | null;
  originCity: string;
  originCountry: string;
  originLatitude: number | null;
  originLongitude: number | null;
  originPostal: string;
  originRadiusMiles: number;
  originState: string;
  overDimension: boolean;
  hazmatRequired: boolean;
  rampsRequired: boolean;
  tarpRequired: boolean;
  teamRequired: boolean;
};

export type TruckTransportT = {
  consistentTruckId: number | null;
  isConsistent: boolean;
  isFromPreferredLane?: boolean;
  capacityStatus: TruckCapacityStatusEnum;
  carrierId: number;
  createdBy: number;
  createdByUserName: string;
  createdDate: string;
  destinationCity: string;
  destinationCountry: string;
  destinationEmptyDate: string;
  destinationLatitude: number | undefined;
  destinationLongitude: number | undefined;
  destinationPostal: string;
  destinationRadiusMiles: number | undefined;
  destinationState: string;
  equipment: string;
  id: number;
  lastMatchAttempted: unknown;
  modifiedBy: number | undefined;
  modifiedDate: number | undefined;
  notes: string | undefined;
  originCity: string;
  originCountry: string;
  originEmptyDate: string;
  originLatitude: number | undefined;
  originLongitude: number | undefined;
  originPostal: string;
  originRadiusMiles: number;
  originState: string;
  overDimension: boolean;
  hazmatRequired: boolean;
  rampsRequired: boolean;
  tarpRequired: boolean;
  teamRequired: boolean;
};

export const initTruck: TruckTransportT = {
  consistentTruckId: null,
  isConsistent: false,
  capacityStatus: TruckCapacityStatusEnum.HasCapacity,
  carrierId: 0,
  createdBy: 0,
  createdByUserName: '',
  createdDate: '',
  destinationCity: '',
  destinationCountry: '',
  destinationEmptyDate: '',
  destinationLatitude: undefined,
  destinationLongitude: undefined,
  destinationPostal: '',
  destinationRadiusMiles: undefined,
  destinationState: '',
  equipment: '',
  id: 0,
  lastMatchAttempted: undefined,
  modifiedBy: undefined,
  modifiedDate: undefined,
  notes: undefined,
  originCity: '',
  originCountry: '',
  originEmptyDate: '',
  originLatitude: undefined,
  originLongitude: undefined,
  originPostal: '',
  originRadiusMiles: 0,
  originState: '',
  overDimension: false,
  hazmatRequired: false,
  rampsRequired: false,
  tarpRequired: false,
  teamRequired: false,
};

export type EquipmentT = {
  operationType: string;
  id: number;
  category: string;
  name: string;
  dataEquipment: unknown | null;
};

export type BidNoteT = {
  operationType: string;
  id: number;
  truckBidId: number;
  key: string;
  value: string;
  createdBy: number;
  createdDate: string;
  modifiedBy: number | null;
  modifiedDate: number | null;
  historyContext: unknown;
};

export type CreateOfferForLoadResponseT = {
  id: number;
  truckId: number;
  carrierId: number;
  loadId: number;
  amount: number;
  bidType: string | null;
  status: string;
  archived: boolean;
  reviewedBy: number | null;
  reviewedDate: string | null;
  createdDate: string;
  createdBy: number;
  modifiedDate: string | null;
  modifiedBy: number | null;
  counterAmount: number | null;
  counteredBy: string | null;
  externalContactName?: string | null;
  externalContactEmail?: string | null;
  externalContactPhone?: string | null;
  externalPreferredContactMethod?: string | null;
  noteKeyValues: BidNoteT[];
};

export type TruckBidT = {
  id: number;
  truckId: number;
  carrierId: number;
  loadId: number;
  amount: number | null;
  bidType: string;
  status: string;
  archived: boolean;
  reviewedBy: number | null;
  reviewedDate: string | null;
  createdDate: string;
  createdBy: number;
  modifiedDate: string | null;
  modifiedBy: number | null;
  counterAmount: number | null;
  requestedBy: string;
  externalContactName?: string | null;
  externalContactEmail?: string | null;
  externalContactPhone?: string | null;
  externalPreferredContactMethod?: string | null;
  noteKeyValues: BidNoteT[];
};

export type SideCardBidStatusT =
  | 'Pending'
  | 'Approved'
  | 'Expired'
  | 'Denied'
  | 'Booked'
  | 'Rejected'
  | 'Assigned'
  | 'Deleted'
  | 'Countered';

export type SideCardBidT = {
  id: number;
  userId: number;
  truckId: number;
  status: SideCardBidStatusT;
  bidType: string;
  amount: number | null;
  requestedBy: string;
  createdDate: string;
  carrierName: string;
  carrierStatus: string;
  mc: string;
  dot: string;
  primaryContact: string;
  primaryPhone: string;
  originLatitude: number | null;
  originLongitude: number | null;
  originRadiusMiles: number | null;
  destinationRadiusMiles: number | null;
  destinationLatitude: number | null;
  destinationLongitude: number | null;
  counterAmount: number | null;
  counteredBy: string | null;
  reviewedDate: string | null;
  externalContactName?: string | null;
  externalContactEmail?: string | null;
  externalContactPhone?: string | null;
  externalPreferredContactMethod?: string | null;
  pointOfSale?: string;
  carrierId: number;
};

export const initCarrier: Carrier = {
  id: 0,
  name: '',
  contacts: [],
  dot: '',
  mc: '',
  address: {
    city: '',
    state: '',
    zip: '',
    line1: '',
    line2: '',
    name: '',
    phone: '',
    phoneAreaCode: '',
    phoneSevenDigit: '',
    code: '',
    country: '',
    contact: '',
    contactLastName: '',
    contactFirstName: '',
  },
  rating: 0,
  numRatings: 0,
  status: '',
  carrierActionPlan: false,
  officeRating: 0,
  isActive: false,
  hasExpiredCargoInsurance: false,
  hasExpiredLiabilityInsurance: false,
  cargoExpiration: '',
  liabilityExpiration: '',
  warnings: [],
  advancesAllowed: false,
  mode: '',
  blacklisted: false,
  firstLoad: false,
  corporatePhoneNumber: '',
  factors: false,
  fallouts: 0,
  totalLoads: 0,
  ltlEnabled: false,
  transfloEnabled: false,
  brokerAuthority: false,
  currencyOptionEnabled: false,
  requireMacropointTracking: null,
  shipperVerifyMCDotNumber: null,
  carrierInsightViewModels: [],
  hasWatchDogAlerts: null,
  hasIdentityAlerts: null,
  hasHwyReeferRestriction: null,
  canBypassHwyReeferRestriction: null,
  hasReeferInsurance: null,
  highwayUrl: null,
  failingHighwayRuleCodingsCount: null,
  carrierReactivationRequestCacheItemViewModel: null,
  isHighwayEnabled: false,
};

export const initialCarrierDefaults: carrierDefaultsT = {
  carrierId: 0,
  defaultContactId: null,
  noCapacityEndDate: null,
  carrierOwnerUserId: 0,
  carrierOwnerUserName: null,
  carrierOwnerUserActive: true,
  defaultEquipment: '',
  defaultDestinationCity: '',
  defaultDestinationState: '',
  defaultDestinationPostal: '',
  defaultDestinationCountry: 'US',
  defaultDestinationRadiusMiles: 150,
  createdBy: null,
  createdDate: null,
  modifiedBy: 0,
  modifiedDate: '',
  defaultOriginCity: '',
  defaultOriginState: '',
  defaultOriginPostal: '',
  defaultOriginCountry: 'US',
  defaultOriginEmptyDate: new Date().toISOString().split('T')[0],
  defaultOriginLatitude: null,
  defaultOriginLongitude: null,
  defaultDestinationLatitude: null,
  defaultDestinationLongitude: null,
  defaultOriginRadiusMiles: 150,
  defaultDestinationEmptyDate: null,
};

type BidOfLoadResponseObjectT = {
  totalMileage: number | null;
  loadNumber: string;
  loadStatus: string;
  stops: StopViewModel[];
  equipmentType: string | null;
  weight: string | null;
  cargoValue: number;
  commodity: string | null;
  temperature: string | null;
  maxRate: number | null;
  notes: string | null;
  teamRequired: boolean;
  hazmatRequired: boolean;
  tarpRequired: boolean;
  overDimension: boolean;
  rampsRequired: boolean;
  loadId: number | null;
  open: boolean;
  truckId: number | null;
  orderId: string | null;
  customerPayLineItems: OrderLineItem[];
  datMedianRate: number | null;
};

export type AllBidsOfLoadResponseT = BidOfLoadResponseObjectT & {
  bids: SideCardBidT[];
};

export type AllBids = {
  totalMileage: number;
  stops: StopViewModel[];
  loadNumber: string;
  orderId: string;
  loadStatus: string;
  equipmentType: string | null;
  weight: string | null;
  commodity: string | null;
  temperature: string | null;
  maxRate: number | null;
  datMedianRate: number | null;
  notes: string | null;
  teamRequired: boolean;
  hazmatRequired: boolean;
  tarpRequired: boolean;
  overDimension: boolean;
  rampsRequired: boolean;
  bids: SideCardBidT[];
  cargoValue: number;
  customerPayLineItems: OrderLineItem[];
  totalCustomerPay: number;
};

export type DrawerStoreT = AllBidsOfLoadResponseT & {
  yourBid: SideCardBidT | null;
  bidId?: number;
};

export const defaultBid: DrawerStoreT = {
  totalMileage: 0,
  loadNumber: '',
  loadStatus: '',
  stops: [],
  equipmentType: null,
  weight: null,
  commodity: null,
  temperature: null,
  maxRate: null,
  notes: null,
  teamRequired: false,
  hazmatRequired: false,
  tarpRequired: false,
  overDimension: false,
  rampsRequired: false,
  yourBid: null,
  loadId: null,
  open: false,
  cargoValue: 0,
  truckId: null,
  orderId: null,
  bids: [],
  customerPayLineItems: [],
  datMedianRate: null,
};

export type BidResponseT = {
  subject: string;
  body: string;
};

export type OfferT = {
  amount: number;
  counterAmount: number | null;
  id: number;
  counteredBy: number;
  createdBy: number;
  createdDate: string;
  capacityStatus: TruckBidStatusEnum;
  destinationEmptyDate: string | null;
  destinationRadiusMiles: number;
  originEmptyDate: string;
  offers: number;
  loadId: number;
  orderId: number;
  customerName: string;
  customerId: number;
  mcNumber: string;
  carrierName: string;
  status: string; // Load status
  assignedTo: string;
  accountManager: string;
  origin: string;
  originCity: string;
  originState: string;
  originCountry: string;
  originZip: string;
  mode: string;
  destination: string;
  destinationCity: string;
  destinationState: string;
  destinationCountry: string;
  destinationPostal: string;
  pickDate: string;
  pickUpTime: string;
  dropDate: string;
  dropOffTime: string;
  equipment: string;
  salesManagerId: null;
  accountManagerId: number;
  loadNumber: string;
  stopPickup: null;
  stopDropoff: null;
  carrierId: number;
  totalBids: number;
  originLatitude: number | null;
  originLongitude: number | null;
  loadCreatedDateUtc: string;
  reviewedDate?: string;
};

export enum TruckBidStatusEnum {
  Pending = 0,
  Approved = 1,
  Denied = 2, // Rejected
  Booked = 3,
  Assigned = 4,
  Deleted = 5,
  Countered = 6,
}
